import React, { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { PopcornIcon } from '@/atoms/Icons/Popcorn'
import { QrIcon } from '@/atoms/Icons/QrIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { TomatoIcon } from '@/atoms/Icons/Tomato'
import { VerifiedHotIcon } from '@/atoms/Icons/VerifiedHotIcon'
import { Image } from '@/atoms/Image'
import { PillBadge } from '@/atoms/PillBadge'
import { Text } from '@/atoms/Text'
import { useDisclose } from '@/hooks/useDisclose'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import FullScreenConfetti from '@/organisms/FullScreenConfetti'
import { ShareGuildTicketsModal } from '@/organisms/ShareGuildTicketsModal/ShareGuildTickets'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { BackgroundVideo } from '@/views/PreSales/PreSalesGiveawayView/components/Header/BackgroundVideo'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { getHoursMinutes } from '@/views/TicketCheckoutViews/utils'
import { BrandLogo } from '@/views/TicketsLandingView/components/BrandLogo'
import { MovieTicketShowtimeInfo } from '@/views/TicketsLandingView/components/MovieTicketShowtimeInfo'
import { ShareButton } from '@/views/TicketsLandingView/components/ShareButton'
import { TicketQrCodes } from '@/views/TicketsLandingView/components/TicketQrCodes'
import { useGetReleaseActionType, useGetShareInfo, useInTheatersLabel } from '@/views/TicketsLandingView/hooks'
import { CurrentTheatricalRelease } from '@/views/TicketsLandingView/types'
import { DetailItem } from './DetailItem'
import { RadialGradient } from './RadialGradient'

interface Props {
  release: CurrentTheatricalRelease
  userTickets: SimpleTicketItem[]
}

export const Hero: FC<Props> = ({ release, userTickets }) => {
  const { t } = useTranslate('tickets')
  const { track } = useSafeAnalytics()
  const { locale } = useLocale()
  const { isOpen: isTrailerOpen, onClose: onTrailerClose, onOpen: onTrailerOpen } = useDisclose(false)
  const [selectedUserTicket, setSelectedUserTicket] = useState<SimpleTicketItem | null>(userTickets?.[0] ?? null)
  const {
    isOpen: isShareGuildCodesModalOpen,
    onClose: onShareGuildCodesModalClose,
    onOpen: openShareGuildCodesModal,
  } = useDisclose(false)

  const setUserTicket = useCallback((ticket: SimpleTicketItem) => setSelectedUserTicket(ticket), [])

  const {
    nonTitleArt16x9Image,
    nonTitleArt1x1Image,
    logo,
    rating,
    duration,
    trailerUrl,
    tomatoesScore,
    audienceScore,
    theatricalSlug,
    ticketPageLink,
    guildCodes,
    backgroundVideo,
    title,
  } = release

  const inTheatersLabel = useInTheatersLabel(release.releaseDate)
  const actionType = useGetReleaseActionType(release, userTickets)
  const shareInfo = useGetShareInfo(release)
  const hasTicketsToday = actionType === 'TICKETS_TODAY'

  const guildTicketsText = useMemo(() => {
    if (!guildCodes?.length) return ''

    return t('useGuildTicketCodes', {
      count: guildCodes.length,
      defaultValue_one: 'Use Your Guild Ticket',
      defaultValue_other: 'Use Your {{count}} Guild Tickets',
    })
  }, [guildCodes?.length, t])

  const handleHeroCtaClicked = () => {
    track('Tickets Landing Hero CTA Clicked', {
      release,
    })
  }

  const handleGuildTicketsClick = () => {
    openShareGuildCodesModal()
  }

  return (
    <div className="relative flex h-full flex-col bg-black pb-12 md:min-h-[90vh]">
      <div style={{ aspectRatio: 1 }} className="relative w-full md:absolute md:inset-0 md:h-full">
        {/*mobile*/}
        <Image src={nonTitleArt1x1Image} sizes="100vw" alt="hero" fill priority className="object-cover md:hidden" />

        {/*desktop*/}
        <Image
          src={nonTitleArt16x9Image}
          alt="hero"
          fill
          priority
          sizes="(min-width: 768px) 100vw"
          className="hidden object-cover object-center md:block"
        />
        {backgroundVideo && !hasTicketsToday && (
          <BackgroundVideo theatricalSlug={theatricalSlug} videoSrc={backgroundVideo} />
        )}
        <LinearGradient className="md:hidden" />
        <BackgroundOverlay className="hidden md:block" />
        {hasTicketsToday && <BackgroundOverlay className="bg-black/50 backdrop-blur md:hidden" />}
        {/*mobile logo*/}
        <BrandLogo
          className={classNames(
            '!absolute left-1/2 -translate-x-1/2 md:hidden',
            hasTicketsToday ? 'scale-75 -bottom-4' : 'bottom-2',
          )}
          cloudinaryPath={logo}
        />
        {hasTicketsToday && <FullScreenConfetti className="md:hidden" recycle={false} />}

        {userTickets.length > 0 && (
          <TicketQrCodes setUserTicket={setUserTicket} className="z-20 md:hidden" userTickets={userTickets} />
        )}
      </div>

      <div className="relative z-10 mt-auto flex flex-col items-center text-center">
        {/*desktop logo*/}
        <BrandLogo className="hidden md:block" cloudinaryPath={logo} />
        <div className="gap mt-4 flex items-center gap-3 md:gap-4">
          {rating && <DetailItem text={rating} />}
          <GuildRecommendedAge projectSlug={theatricalSlug} compact />
          {duration && <DetailItem text={getHoursMinutes(duration / 60, locale)} />}
          {tomatoesScore && (
            <DetailItem text={`${tomatoesScore}%`} icon={<TomatoIcon className="size-4 md:size-6" />} />
          )}
          {audienceScore && (
            <DetailItem
              text={`${audienceScore}%`}
              icon={
                release.isVerifiedHot ? (
                  <VerifiedHotIcon className="size-4 md:size-6" />
                ) : (
                  <PopcornIcon className="-mr-0.5 size-4 md:size-6" />
                )
              }
            />
          )}
        </div>
        <div className="mt-3 flex flex-col items-center space-y-3 md:mt-4 md:space-y-2">
          {actionType === 'GUILD_TICKETS' && guildCodes && (
            <PillBadge size="sm" variant="blue" className="md:pill-badge-md">
              <TicketIcon className="md:size-3.5" size={12} color="info-200" />
              {t('guildTicketsAvailableCount', {
                count: guildCodes.length,
                numberOfGuildCodes: guildCodes.length,
                defaultValue_one: '{{numberOfGuildCodes}} Guild Ticket Available',
                defaultValue_other: '{{numberOfGuildCodes}} Guild Tickets Available',
              })}
            </PillBadge>
          )}

          {actionType === 'TICKETS_TODAY' && (
            <PillBadge size="sm" variant="blue" className="md:pill-badge-md">
              <TicketIcon className="md:size-3.5" size={12} color="info-200" />

              {t('userTicketsPillV2', {
                count: userTickets.length,
                userTicketsQty: userTickets.length,
                defaultValue_one: '{{userTicketsQty}} Ticket',
                defaultValue_other: '{{userTicketsQty}} Tickets',
              })}
            </PillBadge>
          )}
          <Text weight="semibold" color="white" className="photon-title-xs md:photon-heading-xs block">
            {hasTicketsToday ? t('yourMovieIsToday', 'Your Movie is Today!') : inTheatersLabel}
          </Text>
        </div>

        {hasTicketsToday && selectedUserTicket && (
          <MovieTicketShowtimeInfo
            theaterName={selectedUserTicket.venueSummary?.name}
            time={selectedUserTicket.showtimeSummary?.localStartTimeFriendlyString}
          />
        )}

        <div
          className={classNames(
            'mt-6 flex w-full gap-2 px-8 md:mt-10 md:w-fit md:flex-row max-w-[550px] md:max-w-none',
            trailerUrl ? 'flex-col' : 'flex-row',
          )}
        >
          {hasTicketsToday && (
            <LinkButton
              onClick={handleHeroCtaClicked}
              href="/account?t=myTickets"
              size="md"
              className="md:btn-lg hidden grow-1 items-center gap-1.5 rounded-xl md:flex"
              variant="white"
            >
              <QrIcon color="black" className="md:size-[26px]" size={24} />
              {t('viewTickets', 'View Tickets')}
            </LinkButton>
          )}

          {actionType === 'SHOWTIMES' && (
            <LinkButton
              href={ticketPageLink}
              onClick={handleHeroCtaClicked}
              size="md"
              className="md:btn-lg flex grow-1 items-center gap-1.5 rounded-xl"
              variant="white"
            >
              <TicketIcon size={24} color="black" />
              {t('getShowtimes', 'Get Showtimes')}
            </LinkButton>
          )}

          {actionType === 'PRESALE' && (
            <LinkButton
              href={ticketPageLink}
              onClick={handleHeroCtaClicked}
              size="md"
              className="md:btn-lg flex grow-1 items-center gap-1.5 rounded-xl"
              variant="white"
            >
              <TicketIcon size={24} color="black" />
              {t('getShowtimes', 'Get Showtimes')}
            </LinkButton>
          )}

          {actionType === 'GIVEAWAY' && (
            <LinkButton
              onClick={handleHeroCtaClicked}
              href={`/giveaway/${theatricalSlug}`}
              size="md"
              className="md:btn-lg flex grow-1 items-center gap-1.5 rounded-xl"
              variant="white"
            >
              <TicketIcon size={24} color="black" />
              {t('preOrderTickets', 'Pre-Order Tickets')}
            </LinkButton>
          )}

          {actionType === 'GUILD_TICKETS' && guildCodes && (
            <Button
              onClick={handleGuildTicketsClick}
              size="md"
              className="md:btn-lg flex grow-1 items-center gap-1.5 rounded-xl"
              variant="white"
            >
              <GuildIcon size={24} color1="black" />
              {guildTicketsText}
            </Button>
          )}

          <div className="flex items-center gap-2">
            {trailerUrl && !hasTicketsToday && (
              <Button
                size="md"
                className="md:btn-lg flex w-full items-center gap-1.5 rounded-xl md:bg-white md:text-black md:hover:border-core-gray-200  md:hover:bg-core-gray-200"
                variant="offwhite"
                onClick={onTrailerOpen}
              >
                <PlayIcon color="none" className="fill-white md:fill-black" size={24} />
                {t('watchTrailer', 'Watch Trailer')}
              </Button>
            )}

            {!hasTicketsToday && <ShareButton {...shareInfo} className="md:hidden" />}
          </div>
        </div>
      </div>

      <ShareButton
        {...shareInfo}
        buttonVariant="copper"
        className="absolute bottom-6 right-6 z-50 hidden items-center md:flex"
      />
      <ShareGuildTicketsModal
        open={isShareGuildCodesModalOpen}
        onClose={onShareGuildCodesModalClose}
        theatricalSlug={theatricalSlug}
        projectName={title}
        showtimesLink={
          actionType === 'GUILD_TICKETS' && guildCodes
            ? `/tickets/${theatricalSlug}?discounts=${guildCodes.join(',')}`
            : ''
        }
        source="tickets"
      />
      {trailerUrl && (
        <YoutubeVideoModal handleClose={onTrailerClose} showModal={isTrailerOpen} youTubeUrl={trailerUrl} />
      )}

      <RadialGradient
        className="absolute left-0 top-full -z-10 -translate-x-1/2 -translate-y-1/2 opacity-10"
        from="50% 50% at 50% 50%, #21CEA0 0%"
        to="rgba(33, 206, 160, 0) 100%"
        size="200%"
      />
    </div>
  )
}

const LinearGradient = ({ className }: { className?: string }) => (
  <div
    style={{
      background:
        'linear-gradient(0deg, #000000 0%, rgba(0, 0, 0, 0.991353) 5.85%, rgba(0, 0, 0, 0.96449) 11.71%, rgba(0, 0, 0, 0.91834) 17.56%, rgba(0, 0, 0, 0.852589) 23.41%, rgba(0, 0, 0, 0.768225) 29.27%, rgba(0, 0, 0, 0.668116) 35.12%, rgba(0, 0, 0, 0.557309) 40.97%, rgba(0, 0, 0, 0.442691) 46.82%, rgba(0, 0, 0, 0.331884) 52.68%, rgba(0, 0, 0, 0.231775) 58.53%, rgba(0, 0, 0, 0.147411) 64.38%, rgba(0, 0, 0, 0.0816599) 70.24%, rgba(0, 0, 0, 0.03551) 76.09%, rgba(0, 0, 0, 0.0086472) 81.94%, rgba(0, 0, 0, 0) 87.8%)',
    }}
    className={classNames('absolute inset-x-0 bottom-0 h-[200px] w-full md:inset-0 md:h-full', className)}
  />
)

const BackgroundOverlay = ({ className }: { className?: string }) => (
  <div className={classNames('absolute inset-0 bg-black/50', className)} />
)
